import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { useDebounce } from '@/hooks/useDebounce';
import { isBlocked } from '@/lib/blocklist';
import { getImageUrl } from '@/lib/polaroid-utils';

/** Validate a kind 20 event has at least one valid image URL and isn't blocked. */
function isValidPicture(event: NostrEvent): boolean {
  if (event.kind !== 20) return false;
  if (!getImageUrl(event)) return false;
  if (isBlocked(event)) return false;
  return true;
}

/** Validate a kind 1111 comment is on a kind 20 photo. */
function isValidComment(event: NostrEvent): boolean {
  if (event.kind !== 1111) return false;
  if (isBlocked(event)) return false;
  // Must have some content
  if (!event.content.trim()) return false;
  // Must have a K tag (root kind) of "20" — only comments on kind 20 photos
  const rootKind = event.tags.find(([name]) => name === 'K')?.[1];
  if (rootKind !== '20') return false;
  return true;
}

export interface SearchPostsResult {
  pictures: NostrEvent[];
  comments: NostrEvent[];
}

/**
 * Search for kind 20 posts and kind 1111 comments using NIP-50 full-text search.
 * Returns pictures and comments in separate arrays.
 */
export function useSearchPosts(query: string) {
  const { nostr } = useNostr();
  const debouncedQuery = useDebounce(query, 300);

  return useQuery<SearchPostsResult>({
    queryKey: ['search-posts', debouncedQuery],
    queryFn: async ({ signal }) => {
      if (!debouncedQuery.trim()) return { pictures: [], comments: [] };

      // NIP-50 search: two filters in one query
      // 1. Kind 20 pictures matching the search text
      // 2. Kind 1111 comments on kind 20 photos (K tag = "20") matching the search text
      const events = await nostr.query(
        [
          { kinds: [20], search: debouncedQuery.trim(), limit: 20 },
          { kinds: [1111], '#K': ['20'], search: debouncedQuery.trim(), limit: 20 },
        ],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(8000)]) },
      );

      const pictures = events.filter(isValidPicture);
      const comments = events.filter(isValidComment);

      // Sort both by recency
      pictures.sort((a, b) => b.created_at - a.created_at);
      comments.sort((a, b) => b.created_at - a.created_at);

      return { pictures, comments };
    },
    enabled: debouncedQuery.trim().length >= 1,
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });
}
